import { z } from '@customerio/hydra-validation';
import type { AddonSku } from '@/services/billing/addon-change';
import { BILLING_TIME_ZONE } from '@/services/billing/time-zone';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import { ACTIVITY_HISTORY_WINDOWS } from './activity-history';

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: BILLING_TIME_ZONE });

export function formatShortDate(unixSeconds: number): string {
  return SHORT_DATE.format(new Date(unixSeconds * 1000));
}

const SHORT_DATE_WITH_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: BILLING_TIME_ZONE,
});
export function formatShortDateWithYear(unixSeconds: number): string {
  return SHORT_DATE_WITH_YEAR.format(new Date(unixSeconds * 1000));
}

export type AddonCadence = 'monthly' | 'one-time';

export type AddonKey =
  | 'segments'
  | 'activity-history'
  | 'stored-profiles'
  | 'flex-recharge'
  | 'flex-recharge-auto'
  | 'generic';

export type AddonRequest =
  | { key: 'segments'; includedCount: number }
  /**
   * `stored` is the people and objects stored against `limit`, the account's stored-profile limit.
   * `sku` is the pack to select first. The modal lists every pack.
   */
  | { key: 'stored-profiles'; stored: number; limit: number; sku?: AddonSku }
  | { key: 'flex-recharge' | 'flex-recharge-auto'; usedPercent: number }
  | { key: 'activity-history' | 'generic' };

/** Checks a request that crosses from an embedded app, where the `AddonRequest` type can't follow. */
export const addonRequestSchema = z.union([
  z.object({ key: z.literal('segments'), includedCount: z.number().int().positive() }),
  z.object({
    key: z.literal('stored-profiles'),
    stored: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    sku: z.string().optional(),
  }),
  z.object({ key: z.enum(['flex-recharge', 'flex-recharge-auto']), usedPercent: z.number() }),
  z.object({ key: z.enum(['activity-history', 'generic']) }),
]) satisfies z.ZodType<AddonRequest>;

export type AfterLineRun = { text: string; isStrong?: boolean } | 'arrow';

export type AddonConfig = {
  title: string;
  offerName: string;
  cadence: AddonCadence;
  priceUnit?: string;
  goodToKnow: readonly string[];
  hasAutoRecharge: boolean;
};

/** `priceCents` is `null` while the add-ons list, which prices every add-on with a SKU, loads or when it fails. */
export type AddonOffer = AddonConfig & { priceCents: number | null; afterLine: readonly AfterLineRun[] };

/** Dates are unix seconds. */
export type AddonProration = {
  today: number;
  nextBillingDate: number;
  daysLeft: number;
  daysInCycle: number;
  proratedCents: number;
};

const MONTHLY_BILLING = ['Prorated now, then billed on the 1st each month', 'Turn it off any time from Billing'];

export const FLEX_RECHARGE_FULL_CENTS = 1000;
/** Placeholder floor from the prototype: Stripe's fixed fee makes tiny refills uneconomical. */
export const FLEX_RECHARGE_MIN_CENTS = 50;

/** Usage can run past 100% while limits aren't enforced, but a recharge never costs more than a full one. */
export function clampRechargePercent(usedPercent: number): number {
  return Math.min(usedPercent, 100);
}

function proratedRechargeCents(usedPercent: number): number {
  return Math.round((FLEX_RECHARGE_FULL_CENTS * clampRechargePercent(usedPercent)) / 100);
}

/** A recharge costs the share of the gauge used, so it always takes usage back to 0%. */
export function flexRechargeCents(usedPercent: number): number {
  return Math.max(FLEX_RECHARGE_MIN_CENTS, proratedRechargeCents(usedPercent));
}

export function isFlexRechargeAtMinimum(usedPercent: number): boolean {
  return proratedRechargeCents(usedPercent) < FLEX_RECHARGE_MIN_CENTS;
}

const FLEX_RECHARGE: AddonConfig = {
  title: 'Flex recharge',
  offerName: 'Flex recharge',
  cadence: 'one-time',
  goodToKnow: [
    'Billed once to your card, never from your credit balance',
    "Doesn't renew, so recharge whenever you need to",
  ],
  hasAutoRecharge: false,
};

const ADDONS = {
  segments: {
    title: 'Data-driven segments',
    offerName: 'Segments add-on',
    cadence: 'monthly',
    goodToKnow: ['Manual segments have no cap', ...MONTHLY_BILLING],
    hasAutoRecharge: false,
  },
  'activity-history': {
    title: 'See up to a year of history',
    offerName: 'Data retention',
    cadence: 'monthly',
    priceUnit: 'per billing cycle',
    goodToKnow: [
      "Turn it off any time in Billing. Your data isn't deleted. You just won't see it until you turn the add-on back on.",
    ],
    hasAutoRecharge: false,
  },
  'stored-profiles': {
    title: 'Stored-profile packs',
    offerName: 'Stored profiles',
    cadence: 'monthly',
    priceUnit: 'per billing cycle',
    goodToKnow: [
      "Storage isn't metered, so there are no per-profile charges",
      'Sending is never affected by stored-profile limits',
      'Prorated now, then billed each billing cycle',
    ],
    hasAutoRecharge: false,
  },
  'flex-recharge': FLEX_RECHARGE,
  'flex-recharge-auto': { ...FLEX_RECHARGE, hasAutoRecharge: true },
  generic: {
    title: 'Add-on name',
    offerName: 'Add-on',
    cadence: 'monthly',
    goodToKnow: ['What stays included for free', ...MONTHLY_BILLING],
    hasAutoRecharge: false,
  },
} satisfies Record<AddonKey, AddonConfig>;

/** `about` says in one plain line what the data is. */
export type RetentionUnlock = { data: string; about: string; includedDays: number; addonDays: number };

export const RETENTION_UNLOCKS: readonly RetentionUnlock[] = [
  {
    data: 'Metrics',
    about: 'The delivery and engagement numbers in your dashboards and reports.',
    includedDays: 90,
    addonDays: 365,
  },
  {
    data: 'Deliveries & journeys',
    about: 'Each message you sent, and the path each person took through a workflow.',
    ...ACTIVITY_HISTORY_WINDOWS.message,
  },
  {
    data: 'Condition lookback',
    about: 'How far back segment and workflow conditions can check what people did.',
    includedDays: 30,
    addonDays: 365,
  },
  {
    data: 'Profile activity log',
    about: "The events and attribute changes on each person's or object's profile.",
    ...ACTIVITY_HISTORY_WINDOWS.profile,
  },
  {
    data: 'Audit log',
    about: 'A record of who on your team changed what in the account.',
    includedDays: 7,
    addonDays: 30,
  },
];

/**
 * The add-on a modal variant buys. A Flex recharge buys the agent usage reset instead. Any other
 * variant with none still collects a card, but its confirm buys nothing yet.
 */
export function addonSku(request: AddonRequest): AddonSku | undefined {
  const key = request.key;
  switch (key) {
    case 'segments':
      return 'data_driven_segments';
    case 'activity-history':
      return 'data_retention';
    case 'stored-profiles':
      return request.sku;
    case 'flex-recharge':
    case 'flex-recharge-auto':
    case 'generic':
      return undefined;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

/** Whole dollars ("$10") unless the price has cents ("$9.50"). */
export function formatAddonPriceCents(cents: number): string {
  return formatCentsToUsd(cents, cents % 100 === 0 ? 0 : 2);
}

/** "$10 per billing cycle", or `null` when the price is unknown. */
export function perBillingCycle(price: string | null): string | null {
  return price == null ? null : `${price} per billing cycle`;
}

/** The start of the at-cap sentence, for copy that ends it around its own links. */
export function segmentsAddonRemedy(price: string | null): string {
  const lead = 'Remove the data-driven segment cap with the segments add-on';
  return price == null ? lead : `${lead} for ${price} per billing cycle`;
}

function afterLine(request: AddonRequest): AfterLineRun[] {
  switch (request.key) {
    case 'segments':
      return [
        { text: String(request.includedCount), isStrong: true },
        { text: ' included today ' },
        'arrow',
        { text: ' no cap', isStrong: true },
        { text: ' on data-driven segments' },
      ];
    case 'activity-history':
      return [{ text: 'Covers every workspace on your account' }];
    case 'stored-profiles':
      // The modal replaces it with storagePackAfterLine for the selected pack.
      return [];
    case 'flex-recharge':
    case 'flex-recharge-auto':
      return [{ text: 'Brings your agent usage back to ' }, { text: '0%', isStrong: true }, { text: '.' }];
    case 'generic':
      return [
        { text: 'What this unlocks, stated as ' },
        { text: 'today', isStrong: true },
        { text: ' ' },
        'arrow',
        { text: ' ' },
        { text: 'after', isStrong: true },
      ];
    default: {
      const _exhaustive: never = request;
      return _exhaustive;
    }
  }
}

/** The modal's placeholder variant has no SKU, so nothing prices it. */
const GENERIC_PRICE_CENTS = 1000;

function addonPriceCents(request: AddonRequest, catalogPriceCents: number | null): number | null {
  switch (request.key) {
    case 'flex-recharge':
    case 'flex-recharge-auto':
      return flexRechargeCents(request.usedPercent);
    case 'generic':
      return GENERIC_PRICE_CENTS;
    case 'segments':
    case 'activity-history':
    case 'stored-profiles':
      return catalogPriceCents;
    default: {
      const _exhaustive: never = request;
      return _exhaustive;
    }
  }
}

/** "{limit} today → {pack size} stored profiles" */
export function storagePackAfterLine(limit: number, packSize: number): AfterLineRun[] {
  return [
    { text: formatNumber(limit), isStrong: true },
    { text: ' today ' },
    'arrow',
    { text: ' ' },
    { text: formatNumber(packSize), isStrong: true },
    { text: ' stored profiles' },
  ];
}

/** `catalogPriceCents` is the add-ons list's price for the request's SKU, or `null` until it loads. */
export function addonOffer(request: AddonRequest, catalogPriceCents: number | null): AddonOffer {
  return {
    ...ADDONS[request.key],
    priceCents: addonPriceCents(request, catalogPriceCents),
    afterLine: afterLine(request),
  };
}

export function addonSubline(cadence: AddonCadence): string {
  const lead = cadence === 'monthly' ? 'An add-on billed to your card' : 'A one-time add-on billed to your card';
  return `${lead}, separate from your credit balance`;
}

type PricedOffer = Pick<AddonConfig, 'cadence' | 'priceUnit'> & { priceCents: number };

export function addonPrice(offer: Omit<PricedOffer, 'priceUnit'>): string {
  return offer.cadence === 'monthly' ? formatAddonPriceCents(offer.priceCents) : formatCentsToUsd(offer.priceCents);
}

export function addonCadenceLabel(offer: Pick<AddonConfig, 'cadence' | 'priceUnit'>): string {
  if (offer.priceUnit != null) return offer.priceUnit;
  return offer.cadence === 'monthly' ? 'per month' : 'one time';
}

export function addonPeriodSuffix(offer: Pick<AddonConfig, 'priceUnit'>): string {
  return offer.priceUnit == null ? '/month' : ` ${offer.priceUnit}`;
}

export function addonConfirmLabel(offer: PricedOffer, isAutoRecharge: boolean): string {
  if (isAutoRecharge) return 'Turn on auto-recharge';
  const price = addonPrice(offer);
  return offer.cadence === 'monthly' ? `Add for ${price}${addonPeriodSuffix(offer)}` : `Pay ${price}`;
}

export function prorationTodayLabel(proration: AddonProration): string {
  const range = `${formatShortDate(proration.today)} – ${formatShortDate(proration.nextBillingDate)}`;
  return `Today · ${range} (${proration.daysLeft} of ${proration.daysInCycle} days)`;
}

export type AutoRechargeSettings = {
  isEnabled: boolean;
  thresholdPercent: number | null;
  isMonthlyLimitEnabled: boolean;
  monthlyLimitDollars: number | null;
};

export type AutoRechargeErrors = { threshold?: string; monthlyLimit?: string };

export const DEFAULT_AUTO_RECHARGE_SETTINGS: AutoRechargeSettings = {
  isEnabled: false,
  thresholdPercent: 100,
  isMonthlyLimitEnabled: false,
  monthlyLimitDollars: null,
};

export function autoRechargeErrors(settings: AutoRechargeSettings, rechargeCents: number): AutoRechargeErrors {
  if (!settings.isEnabled) return {};

  const errors: AutoRechargeErrors = {};
  const { thresholdPercent, monthlyLimitDollars } = settings;
  if (
    thresholdPercent == null ||
    !Number.isInteger(thresholdPercent) ||
    thresholdPercent < 1 ||
    thresholdPercent > 100
  ) {
    errors.threshold = 'Enter a whole percent from 1 to 100.';
  }
  if (settings.isMonthlyLimitEnabled && (monthlyLimitDollars ?? 0) * 100 < rechargeCents) {
    errors.monthlyLimit = `Set the limit to at least ${formatCentsToUsd(rechargeCents, rechargeCents % 100 === 0 ? 0 : 2)}.`;
  }
  return errors;
}
